import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Edit, Save, Check, AlertCircle, Package, Zap, Users, X } from 'lucide-react';
import { getPlanManagement, updateEmployerPlan } from '../../services/adminService';
import api from '../../api/api';
import { useToast } from '../ui/Toast';

const PlanManagement = () => {
  const { showToast } = useToast();
  const [planData, setPlanData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingPlan, setEditingPlan] = useState(null);
  const [editedPlan, setEditedPlan] = useState(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [selectedEmployer, setSelectedEmployer] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [updateLoading, setUpdateLoading] = useState(false);
  const [updateSuccess, setUpdateSuccess] = useState(false);
  const [realEmployers, setRealEmployers] = useState([]);
  const [showCustomPlanModal, setShowCustomPlanModal] = useState(false);
  const [customPlanData, setCustomPlanData] = useState({
    employerId: null,
    price: 0,
    jobPostings: 0,
    aiCredits: 0,
    recruiterSeats: 0,
    features: [],
    billingCycle: 'monthly',
    startDate: '', // Empty by default, admin can set future date if needed
    notes: ''
  });

  useEffect(() => {
    fetchPlanData();
    fetchRealEmployers();
  }, []);

  const fetchPlanData = async () => {
    setLoading(true);
    try {
      const response = await getPlanManagement();
      if (response.success) {
        setPlanData(response.data);
      } else {
        setError(response.error || 'Failed to fetch plan data');
      }
    } catch (err) {
      setError('An error occurred while fetching plan data');
    } finally {
      setLoading(false);
    }
  };

  const fetchRealEmployers = async () => {
    try {
      const response = await api.get('admin/users?type=employer');

      if (response.data.success) {
        const employers = response.data.data?.users || [];
        setRealEmployers(employers);
      } else {
        setRealEmployers([]);
      }
    } catch (err) {
      setRealEmployers([]);
    }
  };

  const handleEditPlan = (plan) => {
    setEditingPlan(plan.id);
    setEditedPlan({
      ...plan,
      features: [...plan.features]
    });
  };

  const handleSavePlan = async () => {
    try {
      const response = await api.put('admin/plans/update', { plan: editedPlan });
      
      if (response.data.success) {
        const updatedPlans = planData.plans.map(plan => 
          plan.id === editedPlan.id ? editedPlan : plan
        );

        setPlanData({
          ...planData,
          plans: updatedPlans
        });
        
        setEditingPlan(null);
        setEditedPlan(null);
        
        showToast('Plan updated successfully!', 'success');
      } else {
        showToast('Failed to update plan: ' + (response.data.error || 'Unknown error'), 'error');
      }
    } catch (err) {
      showToast('Failed to update plan. Please try again.', 'error');
    }
  };

  const handleCancelEdit = () => {
    setEditingPlan(null);
    setEditedPlan(null);
  };

  const handleFeatureChange = (index, value) => {
    const updatedFeatures = [...editedPlan.features];
    updatedFeatures[index] = value;
    setEditedPlan({
      ...editedPlan,
      features: updatedFeatures
    });
  };

  const handleAddFeature = () => {
    setEditedPlan({
      ...editedPlan,
      features: [...editedPlan.features, '']
    });
  };

  const handleRemoveFeature = (index) => {
    const updatedFeatures = [...editedPlan.features];
    updatedFeatures.splice(index, 1);
    setEditedPlan({
      ...editedPlan,
      features: updatedFeatures
    });
  };

  const handleShowConfirmModal = (employer, plan) => {
    setSelectedEmployer(employer);
    setSelectedPlan(plan);
    setShowConfirmModal(true);
  };

  const handleUpdateEmployerPlan = async () => {
    if (!selectedEmployer || !selectedPlan) return;
    
    setUpdateLoading(true);
    try {
      const response = await updateEmployerPlan(selectedEmployer.id, {
        planId: selectedPlan.id
      });
      
      if (response.success) {
        setUpdateSuccess(true);
        setTimeout(() => {
          setUpdateSuccess(false);
          setShowConfirmModal(false);
          setSelectedEmployer(null);
          setSelectedPlan(null);
          fetchRealEmployers();
        }, 2000);
      } else {
        setError(response.error || 'Failed to update employer plan');
      }
    } catch (err) {
      setError('An error occurred while updating employer plan');
    } finally {
      setUpdateLoading(false);
    }
  };

  const handleShowCustomPlanModal = (employer) => {
    setCustomPlanData({
      ...customPlanData,
      employerId: employer.id,
      employerName: employer.companyName || employer.name,
      employerEmail: employer.email
    });
    setShowCustomPlanModal(true);
  };

  const handleCustomPlanFeatureChange = (index, value) => {
    const updatedFeatures = [...customPlanData.features];
    updatedFeatures[index] = value;
    setCustomPlanData({ ...customPlanData, features: updatedFeatures });
  };

  const handleAddCustomFeature = () => {
    setCustomPlanData({
      ...customPlanData,
      features: [...customPlanData.features, '']
    });
  };

  const handleRemoveCustomFeature = (index) => {
    const updatedFeatures = [...customPlanData.features];
    updatedFeatures.splice(index, 1);
    setCustomPlanData({ ...customPlanData, features: updatedFeatures });
  };

  const handleAssignCustomPlan = async () => {
    setUpdateLoading(true);
    try {
      const response = await api.post('admin/assign-custom-plan', customPlanData);
      
      if (response.data.success) {
        const paymentLink = response.data.data.payment_link;
        const emailSent = response.data.data.email_sent;
        const employerEmail = response.data.data.employer_email;
        
        // Show success message
        showToast(response.data.data.message, 'success');
        
        if (!emailSent && paymentLink) {
          // Email failed, copy payment link to clipboard
          navigator.clipboard.writeText(paymentLink).then(() => {
            showToast('Payment link copied to clipboard! Please send to: ' + employerEmail, 'info', 5000);
          }).catch(() => {
            showToast('Payment link: ' + paymentLink, 'info', 5000);
          });
        }
        
        setShowCustomPlanModal(false);
        fetchRealEmployers();
        setCustomPlanData({
          employerId: null,
          price: 0,
          jobPostings: 0,
          aiCredits: 0,
          recruiterSeats: 0,
          features: [],
          billingCycle: 'monthly',
          startDate: '',
          notes: ''
        });
      } else {
        showToast('Failed to assign custom plan: ' + (response.data.error || 'Unknown error'), 'error');
      }
    } catch (err) {
      showToast('Failed to assign custom plan. Please try again.', 'error');
    } finally {
      setUpdateLoading(false);
    }
  };

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount);
  };

  // Mock data for development/fallback
  const mockPlanData = {
    plans: [
      {
        id: 'starter',
        name: 'Starter',
        price: 0,
        users: 25,
        features: ['20 job postings', '100 AI credits', 'Basic support']
      },
      {
        id: 'basic',
        name: 'Basic',
        price: 999,
        users: 30,
        features: ['50 job postings', '400 AI credits', '5 recruiter seats']
      },
      {
        id: 'pro',
        name: 'Pro',
        price: 2999,
        users: 15,
        features: ['200 job postings', '2000 AI credits', '10 recruiter seats']
      },
      {
        id: 'enterprise',
        name: 'Enterprise',
        price: 'custom',
        users: 8,
        features: ['Unlimited postings', 'Custom credits', 'Unlimited seats']
      }
    ],
    totalSubscribers: 78,
    monthlyRevenue: 1250000
  };

  // Use only real employers from database
  const displayEmployers = realEmployers;

  // Use API data or fallback to mock data
  const displayData = planData || mockPlanData;
  const plans = displayData?.plans || [];

  if (loading && !planData) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error && !planData) {
    return (
      <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded relative" role="alert">
        <strong className="font-bold">Error: </strong>
        <span className="block sm:inline">{error}</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-gray-900">Plan Management</h2>

      {/* Plan Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {plans.map((plan) => (
          <motion.div
            key={plan.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
            className="bg-white p-6 rounded-lg shadow-md"
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-700 capitalize">{plan.name}</h3>
              <Package className="h-8 w-8 text-blue-500" />
            </div>
            <p className="text-3xl font-bold text-gray-900">{plan.users}</p>
            <p className="mt-2 text-sm text-gray-500">
              Active subscribers
            </p>
            <p className="mt-1 text-sm font-medium text-blue-600">
              {typeof plan.price === 'number' ? formatCurrency(plan.price) : plan.price} / month
            </p>
          </motion.div>
        ))}
      </div>

      {/* Plan Definitions - Editable Cards */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h3 className="text-lg font-medium text-gray-700 mb-6">Edit Plan Details</h3>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {plans.map((plan) => (
            <motion.div
              key={plan.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="border border-gray-200 rounded-xl p-6 hover:shadow-lg transition-shadow"
            >
              {editingPlan === plan.id ? (
                // Edit Mode
                <div className="space-y-4">
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="text-xl font-bold text-gray-900 capitalize">{plan.name} Plan</h4>
                    <div className="flex gap-2">
                      <button
                        onClick={handleSavePlan}
                        className="p-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors"
                        title="Save Changes"
                      >
                        <Save size={18} />
                      </button>
                      <button
                        onClick={handleCancelEdit}
                        className="p-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors"
                        title="Cancel"
                      >
                        <X size={18} />
                      </button>
                    </div>
                  </div>
                  
                  {/* Price */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Monthly Price (₹)</label>
                    <input
                      type="number"
                      value={typeof editedPlan.price === 'number' ? editedPlan.price : 0}
                      onChange={(e) => setEditedPlan({...editedPlan, price: parseInt(e.target.value) || 0})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Enter price"
                    />
                  </div>
                  
                  {/* Features - Editable List */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Features</label>
                    <div className="space-y-2">
                      {editedPlan.features.map((feature, index) => (
                        <div key={index} className="flex gap-2">
                          <input
                            type="text"
                            value={feature}
                            onChange={(e) => handleFeatureChange(index, e.target.value)}
                            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                            placeholder="e.g., 100 job postings"
                          />
                          <button
                            onClick={() => handleRemoveFeature(index)}
                            className="p-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
                            title="Remove feature"
                          >
                            <X size={16} />
                          </button>
                        </div>
                      ))}
                      <button
                        onClick={handleAddFeature}
                        className="w-full px-4 py-2 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-blue-500 hover:text-blue-500 transition-colors text-sm font-medium flex items-center justify-center gap-2"
                      >
                        <Plus size={16} />
                        Add Feature
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                // View Mode
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="text-xl font-bold text-gray-900 capitalize">{plan.name} Plan</h4>
                    <button
                      onClick={() => handleEditPlan(plan)}
                      className="p-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
                      title="Edit Plan"
                    >
                      <Edit size={18} />
                    </button>
                  </div>
                  
                  <div className="mb-4">
                    <p className="text-3xl font-bold text-blue-600">
                      {typeof plan.price === 'number' ? formatCurrency(plan.price) : plan.price}
                      <span className="text-sm font-normal text-gray-500">/month</span>
                    </p>
                  </div>
                  
                  <div className="space-y-2">
                    <p className="text-sm font-semibold text-gray-700">Features:</p>
                    <ul className="space-y-2">
                      {plan.features && plan.features.map((feature, index) => (
                        <li key={index} className="flex items-start gap-2 text-sm text-gray-600">
                          <Check size={16} className="text-green-500 flex-shrink-0 mt-0.5" />
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  
                  {plan.limits && (
                    <div className="mt-4 pt-4 border-t border-gray-200">
                      <p className="text-sm font-semibold text-gray-700 mb-2">Enforced Limits:</p>
                      <div className="grid grid-cols-3 gap-2 text-xs">
                        <div className="bg-blue-50 p-2 rounded">
                          <p className="text-gray-600">Job Postings</p>
                          <p className="font-bold text-blue-600">
                            {plan.limits.jobPostings === -1 ? 'Unlimited' : plan.limits.jobPostings}
                          </p>
                        </div>
                        <div className="bg-green-50 p-2 rounded">
                          <p className="text-gray-600">AI Credits</p>
                          <p className="font-bold text-green-600">{plan.limits.aiCredits}</p>
                        </div>
                        <div className="bg-purple-50 p-2 rounded">
                          <p className="text-gray-600">Recruiter Seats</p>
                          <p className="font-bold text-purple-600">{plan.limits.recruiterSeats}</p>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  <div className="mt-4 pt-4 border-t border-gray-200">
                    <p className="text-sm text-gray-500">
                      <span className="font-semibold text-gray-700">{plan.users}</span> active subscribers
                    </p>
                  </div>
                </div>
              )}
            </motion.div>
          ))}
        </div>
      </div>

      {/* Employer Plan Management */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <div className="p-6">
          <h3 className="text-lg font-medium text-gray-700 mb-4">Employer Plan Management</h3>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Employer</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Current Plan</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {displayEmployers.length === 0 ? (
                  <tr>
                    <td colSpan="4" className="px-6 py-8 text-center">
                      <div className="text-gray-500">
                        <Users size={48} className="mx-auto mb-3 opacity-30" />
                        <p className="text-lg font-medium">No Employers Found</p>
                        <p className="text-sm mt-2">Employers will appear here once they register</p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  displayEmployers.map((employer) => (
                  <tr key={employer.id}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10 flex items-center justify-center bg-gray-100 rounded-full">
                          <Users size={20} className="text-blue-500" />
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">{employer.companyName || employer.name}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{employer.email}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800 capitalize">
                        {employer.subscription?.plan || employer.plan || 'starter'}
                      </span>
                      {employer.subscription?.customPlan && (
                        <span className="ml-2 px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-purple-100 text-purple-800">
                          Custom
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex gap-2">
                        <select
                          className="border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                          defaultValue=""
                          onChange={(e) => {
                            if (e.target.value) {
                              const selectedPlan = plans.find(p => p.id === e.target.value);
                              handleShowConfirmModal(employer, selectedPlan);
                              e.target.value = "";
                            }
                          }}
                        >
                          <option value="" disabled>Change Plan</option>
                          {plans.map((plan) => (
                            <option key={plan.id} value={plan.id} disabled={plan.id === (employer.subscription?.plan || employer.plan)}>
                              {plan.name}
                            </option>
                          ))}
                        </select>
                        <button
                          onClick={() => handleShowCustomPlanModal(employer)}
                          className="px-3 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 transition-colors text-xs font-medium whitespace-nowrap"
                          title="Assign Custom Plan"
                        >
                          Custom Plan
                        </button>
                      </div>
                    </td>
                  </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Confirmation Modal */}
      {showConfirmModal && selectedEmployer && selectedPlan && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-lg shadow-xl max-w-md w-full p-6"
          >
            {updateSuccess ? (
              <div className="text-center py-6">
                <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100">
                  <Check className="h-6 w-6 text-green-600" />
                </div>
                <h3 className="mt-3 text-lg font-medium text-gray-900">Plan Updated Successfully</h3>
                <p className="mt-2 text-sm text-gray-500">
                  The employer's plan has been updated to {selectedPlan.name}.
                </p>
              </div>
            ) : (
              <>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-medium text-gray-900">Confirm Plan Change</h3>
                  <button
                    onClick={() => setShowConfirmModal(false)}
                    className="text-gray-400 hover:text-gray-500"
                  >
                    <X size={20} />
                  </button>
                </div>
                <div className="mb-6">
                  <p className="text-sm text-gray-500">
                    Are you sure you want to change the plan for <span className="font-medium">{selectedEmployer.name}</span> from <span className="font-medium capitalize">{selectedEmployer.currentPlan}</span> to <span className="font-medium">{selectedPlan.name}</span>?
                  </p>
                  <div className="mt-4 p-3 bg-yellow-50 border border-yellow-100 rounded-md flex items-start">
                    <AlertCircle className="h-5 w-5 text-yellow-400 mr-2 flex-shrink-0 mt-0.5" />
                    <p className="text-xs text-yellow-700">
                      This will immediately change the employer's subscription and they will be billed at the new rate (₹{selectedPlan.monthlyPrice}) starting from the next billing cycle.
                    </p>
                  </div>
                </div>
                <div className="flex justify-end space-x-3">
                  <button
                    onClick={() => setShowConfirmModal(false)}
                    className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleUpdateEmployerPlan}
                    disabled={updateLoading}
                    className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                  >
                    {updateLoading ? (
                      <div className="flex items-center">
                        <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white mr-2"></div>
                        Processing...
                      </div>
                    ) : (
                      'Confirm Change'
                    )}
                  </button>
                </div>
              </>
            )}
          </motion.div>
        </div>
      )}

      {/* Custom Plan Assignment Modal */}
      {showCustomPlanModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-lg shadow-xl max-w-2xl w-full m-4"
          >
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-medium text-gray-900">Assign Custom Plan</h3>
                <button
                  onClick={() => setShowCustomPlanModal(false)}
                  className="text-gray-400 hover:text-gray-500"
                >
                  <X size={20} />
                </button>
              </div>
              <p className="mt-2 text-sm text-gray-500">
                Create a custom pricing plan for <span className="font-medium">{customPlanData.employerName}</span>
              </p>
            </div>

            <div className="p-6 space-y-4 max-h-[600px] overflow-y-auto">
              {/* Price */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Monthly Price (₹) <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    value={customPlanData.price}
                    onChange={(e) => setCustomPlanData({...customPlanData, price: parseInt(e.target.value) || 0})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                    placeholder="e.g., 5000"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Billing Cycle <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={customPlanData.billingCycle}
                    onChange={(e) => setCustomPlanData({...customPlanData, billingCycle: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  >
                    <option value="monthly">Monthly</option>
                    <option value="quarterly">Quarterly</option>
                    <option value="annually">Annually</option>
                  </select>
                </div>
              </div>

              {/* Limits */}
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Job Postings <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    value={customPlanData.jobPostings}
                    onChange={(e) => setCustomPlanData({...customPlanData, jobPostings: parseInt(e.target.value) || 0})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                    placeholder="e.g., 100"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    AI Credits <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    value={customPlanData.aiCredits}
                    onChange={(e) => setCustomPlanData({...customPlanData, aiCredits: parseInt(e.target.value) || 0})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                    placeholder="e.g., 1000"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Recruiter Seats <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    value={customPlanData.recruiterSeats}
                    onChange={(e) => setCustomPlanData({...customPlanData, recruiterSeats: parseInt(e.target.value) || 0})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                    placeholder="e.g., 15"
                  />
                </div>
              </div>

              {/* Start Date */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Start Date <span className="text-gray-400 font-normal">(Optional - Leave empty to start immediately)</span>
                </label>
                <input
                  type="date"
                  value={customPlanData.startDate}
                  onChange={(e) => setCustomPlanData({...customPlanData, startDate: e.target.value})}
                  min={new Date(Date.now() + 86400000).toISOString().split('T')[0]}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  placeholder="Leave empty to start now"
                />
                <p className="text-xs text-gray-500 mt-1">If you want to schedule for a future date, select tomorrow or later</p>
              </div>

              {/* Features */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Custom Features
                </label>
                <div className="space-y-2">
                  {customPlanData.features.map((feature, index) => (
                    <div key={index} className="flex gap-2">
                      <input
                        type="text"
                        value={feature}
                        onChange={(e) => handleCustomPlanFeatureChange(index, e.target.value)}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-sm"
                        placeholder="e.g., Priority email support"
                      />
                      <button
                        onClick={() => handleRemoveCustomFeature(index)}
                        className="p-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
                      >
                        <X size={16} />
                      </button>
                    </div>
                  ))}
                  <button
                    onClick={handleAddCustomFeature}
                    className="w-full px-4 py-2 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-purple-500 hover:text-purple-500 transition-colors text-sm font-medium flex items-center justify-center gap-2"
                  >
                    <Plus size={16} />
                    Add Feature
                  </button>
                </div>
              </div>

              {/* Admin Notes */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Admin Notes (Internal)
                </label>
                <textarea
                  value={customPlanData.notes}
                  onChange={(e) => setCustomPlanData({...customPlanData, notes: e.target.value})}
                  rows="3"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  placeholder="Any internal notes about this custom plan..."
                />
              </div>

              {/* Payment Information Notice */}
              <div className="bg-yellow-50 border border-yellow-100 rounded-md p-4">
                <div className="flex items-start">
                  <AlertCircle className="h-5 w-5 text-yellow-400 mr-2 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-yellow-700">
                    <p className="font-medium mb-1">Payment Information</p>
                    <p>After assigning this custom plan, you need to:</p>
                    <ul className="list-disc list-inside mt-1 space-y-1">
                      <li>Contact the employer to discuss payment terms</li>
                      <li>Set up manual invoicing or payment gateway</li>
                      <li>Employer will be charged ₹{customPlanData.price} per {customPlanData.billingCycle === 'monthly' ? 'month' : customPlanData.billingCycle === 'quarterly' ? 'quarter' : 'year'}</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            <div className="p-6 border-t border-gray-200 flex justify-end space-x-3">
              <button
                onClick={() => setShowCustomPlanModal(false)}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleAssignCustomPlan}
                disabled={updateLoading}
                className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 disabled:opacity-50"
              >
                {updateLoading ? (
                  <div className="flex items-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white mr-2"></div>
                    Assigning...
                  </div>
                ) : (
                  'Assign Custom Plan'
                )}
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

// Plus icon component
const Plus = ({ size, className }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <line x1="12" y1="5" x2="12" y2="19"></line>
    <line x1="5" y1="12" x2="19" y2="12"></line>
  </svg>
);

export default PlanManagement;
